import { inject, Inject, Injectable } from '@angular/core';
import { PolicySigners, PolicySignersKeys } from '@ag-common-lib/lib/models/utils/policy-signers.model';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { FirebaseApp } from 'firebase/app';
import { policiesSignersFromFirestore } from 'ag-common-svc/lib/services/user-agreements/user-agreements-service-utils';
import { ToastrService } from 'ngx-toastr';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { catchError, map, Observable, of } from 'rxjs';
import { getMaxVersion } from 'ag-common-svc/lib/utils/last-signing-versions-by-agreement-type.util';
import { policiesTypeMap, PolicyTypes } from '@ag-common-lib/lib/models/utils/policy.model';
import { ChangeSourceType } from '@ag-common-lib/lib/lists/change-logs/change-source-type.enum';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { ChangeSourceTypeService } from 'ag-common-svc/lib/services/change-source-type.service';

@Injectable({
  providedIn: 'root',
})
export class PolicySignersService {
  public readonly fsDao: CommonFireStoreDao<PolicySigners>;
  private readonly policyCollectionPath = 'policy';
  private readonly policySignersCollectionPath = 'policy-signers';
  private changeSourceTypeService = inject(ChangeSourceTypeService);

  private _sourceType: ChangeSourceType = this.changeSourceTypeService.sourceType();

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private toastrService: ToastrService,
  ) {
    this.fsDao = new CommonFireStoreDao<PolicySigners>(fireBaseApp, PolicySignersService.fromFirestore, null);
  }

  static readonly fromFirestore = policiesSignersFromFirestore;

  createSigner(docDbId: string, data: PolicySigners, silent = false) {
    const table = this.getCollectionPath(docDbId);
    const policyType = policiesTypeMap.get(data?.[PolicySignersKeys.policyType]);

    return this.fsDao
      .create(data, table)
      .then(response => {
        !silent && this.toastrService.success(`${policyType} was signed Successfully!`);
        return response;
      })

      .catch(e => {
        console.log('e', e);
      });
  }

  updatePolicySigners(
    policyDbId: string,
    signerDbId: string,
    data: Partial<PolicySigners>,
    action: ChangeSourceType = this._sourceType,
  ) {
    if (!policyDbId || !signerDbId) {
      return Promise.reject(new Error('policyDbId and signerDbId are required to update policy signers.'));
    }

    const table = this.getCollectionPath(policyDbId);
    Object.assign(data, {
      [BaseModelKeys.actionName]: action,
    });
    return this.fsDao.updateFields(data, signerDbId, table);
  }

  getList(docId: string, qp: QueryParam[] = []): Observable<PolicySigners[]> {
    const table = this.getCollectionPath(docId);

    return this.fsDao.getList(table, qp);
  }

  /**
   * Get last signed version as string by agentDbId and type from policy-signers collection
   * @param agentDbId The agent database ID
   * @param policyType The policy type to filter by
   * @returns Observable<string | null> The last signed version or null if none found
   */
  getLastSignedVersionByType(agentDbId: string, policyType: PolicyTypes): Observable<string | null> {
    const qp: QueryParam[] = [
      new QueryParam(PolicySignersKeys.agentDbId, WhereFilterOperandKeys.equal, agentDbId),
      new QueryParam(PolicySignersKeys.policyType, WhereFilterOperandKeys.equal, policyType),
    ];

    return this.getSignersByAgentDbId(agentDbId, qp).pipe(
      map((signers: PolicySigners[]) => {
        if (!signers?.length) {
          return null;
        }

        const versions = signers
          .map(signer => signer?.[PolicySignersKeys.policyVersion])
          .filter((version): version is string => !!version);

        if (versions.length === 0) {
          return null;
        }

        return getMaxVersion(versions) ?? null;
      }),
      catchError(error => {
        console.error('Error getting last signed version by type', error);
        return of(null);
      }),
    );
  }

  getSignersByAgentDbId(agentDbId: string, queryParams: QueryParam[] = []): Observable<PolicySigners[]> {
    const qp: QueryParam[] = [
      new QueryParam(PolicySignersKeys.agentDbId, WhereFilterOperandKeys.equal, agentDbId),
      ...queryParams,
    ];

    return this.fsDao.getCollectionGroupSnapshot(this.policySignersCollectionPath, qp).pipe(
      map(snapshot => {
        return snapshot.docs.map(doc => {
          if (!doc.exists()) {
            return null;
          }
          const data = doc.data();
          const parentAgent = doc?.ref?.parent?.parent;
          const parentDbId = parentAgent?.id;

          return data;
        });
      }),
    );
  }

  private getCollectionPath(userAgreementDbId: string): string {
    return [this.policyCollectionPath, userAgreementDbId, this.policySignersCollectionPath].join('/');
  }
}
